// Tensors as the device sees them (design 02 3.1, 3.4).
//
// Input tensors: a name, dimensions (the built-in 사람 · 월 · 분류(대분류) ·
// 결제수단, or ones people made), a unit and the cells that have a value.
// A cell's coord is its place along each dimension joined by "|": an
// item's id, a month "2026-10", or a number of a range 1..N. A place whose
// item was deleted (or past a range's end) keeps its cell on the server;
// here it is not shown and the engine does not get it.
//
// Computed tensors are defined names (=수식). The server keeps their
// formulas as they are typed and does not read them; before one is saved
// this device checks it (checkName): the engine (WebAssembly in the
// worker) calculates it and gives its dimensions, a #DIM! (dimensions that
// do not fit together) is not saved, nor a name that comes back to itself
// through other names (a circular reference), nor shift/cumsum/diff along
// a dimension whose order is only for showing (3.4: 시간·순서 차원만).
import type { Id, TensorDef, TensorDim, TensorDimKey, TensorEntry, TensorItem } from './api'
import type { EvalValue } from './calc/protocol'
import { dimItems } from './dims'
import { addMonths } from './format'
import { itemsOf } from './views'

export const COORD_SEP = '|'

/** The built-in dimensions a tensor may have, as formulas name them. */
export const BUILTIN_DIMS: { key: TensorDimKey; name: string }[] = [
  { key: 'person', name: '사람' },
  { key: 'month', name: '월' },
  { key: 'category', name: '분류' },
  { key: 'payment', name: '결제수단' },
]

/** "d12" -> 12 (a dimension people made), else 0 */
export function customDimId(key: string): Id {
  const m = /^d([1-9]\d*)$/.exec(key)
  return m ? Number(m[1]) : 0
}

/** The name formulas use for a tensor's dimension (null: a dimension that is gone). */
export function dimKeyName(key: string, dims: Record<number, TensorDim> | undefined): string | null {
  const b = BUILTIN_DIMS.find((d) => d.key === key)
  if (b) return b.name
  const d = dims?.[customDimId(key)]
  return d && !d.deleted ? d.name : null
}

/** "용돈[사람, 월]" */
export function tensorTitle(t: Pick<TensorDef, 'name' | 'dims'>, dims: Record<number, TensorDim> | undefined): string {
  return `${t.name}[${t.dims.map((k) => dimKeyName(k, dims) ?? '?').join(', ')}]`
}

/** The living input tensors, in the order they were made. */
export function inputTensors(tensors: Record<number, TensorDef> | undefined): TensorDef[] {
  return Object.values(tensors ?? {})
    .filter((t) => !t.deleted)
    .sort((a, b) => a.id - b.id)
}

/** One place along a dimension: its part of a coord and how it is shown. */
export interface Place {
  part: string
  label: string
}

/**
 * The places of a tensor's dimension that take a value now: the living
 * items in order (분류: the top-level ones), the months given, 1..N.
 * null when the dimension is gone.
 */
export function placesOf(
  key: string,
  items: Record<number, TensorItem>,
  dims: Record<number, TensorDim> | undefined,
  months: readonly string[],
): Place[] | null {
  const item = (i: TensorItem): Place => ({ part: String(i.id), label: i.name })
  switch (key) {
    case 'person':
      return itemsOf(items, 'person').map(item)
    case 'category':
      return itemsOf(items, 'category').map(item)
    case 'payment':
      return itemsOf(items, 'payment').map(item)
    case 'month':
      return months.map((m) => ({ part: m, label: m }))
  }
  const d = dims?.[customDimId(key)]
  if (!d || d.deleted) return null
  if (d.size !== null) return Array.from({ length: d.size }, (_, i) => ({ part: String(i + 1), label: String(i + 1) }))
  return dimItems(items, d.id).map(item)
}

/**
 * The coord of an input tensor's cell from its item along each dimension
 * as the engine names them (a saved view's cell: 사람 "엄마", 월
 * "2026-10"); null when a place takes no value (an item that is not one,
 * as "(없음)").
 */
export function coordOfNames(
  t: Pick<TensorDef, 'dims'>,
  at: Record<string, string>,
  items: Record<number, TensorItem>,
  dims: Record<number, TensorDim> | undefined,
): string | null {
  const parts: string[] = []
  for (const key of t.dims) {
    const name = dimKeyName(key, dims)
    const item = name === null ? undefined : at[name]
    if (item === undefined) return null
    const places = placesOf(key, items, dims, /^\d{4}-(0[1-9]|1[0-2])$/.test(item) ? [item] : [])
    const p = places?.find((x) => x.label === item)
    if (!p) return null
    parts.push(p.part)
  }
  return parts.join(COORD_SEP)
}

/** The months a tensor editor offers: those of the workbook (first..last),
    up to a year after this month. */
export function editorMonths(workbookMonths: readonly string[], thisMonth: string): string[] {
  const first = workbookMonths[0] && workbookMonths[0] < thisMonth ? workbookMonths[0] : addMonths(thisMonth, -11)
  let last = addMonths(thisMonth, 12)
  const end = workbookMonths[workbookMonths.length - 1]
  if (end && end > last) last = end
  const out: string[] = []
  for (let m = first; m <= last; m = addMonths(m, 1)) out.push(m)
  return out
}

/** The months the cells of these tensors name (for 월). */
export function entryMonths(tensors: readonly TensorDef[], entries: Record<number, Record<string, TensorEntry>> | undefined): string[] {
  const out = new Set<string>()
  for (const t of tensors) {
    const at = t.dims.indexOf('month')
    if (at < 0) continue
    for (const e of Object.values(entries?.[t.id] ?? {})) {
      if (e.value === null) continue
      const m = e.coord.split(COORD_SEP)[at]
      if (m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m)) out.add(m)
    }
  }
  return [...out]
}

/** A value typed into a tensor's cell: like the 표 screen's numbers
    ("1,234", "-3.5", "12%", "1e3"); "" clears; undefined when it is no number. */
export function parseCellNumber(text: string): number | null | undefined {
  const t = text.trim().replace(/\s+/g, '')
  if (t === '') return null
  const m = /^([+-]?)(\d{1,3}(?:,\d{3})+|\d*)(\.\d+)?(?:[eE]([+-]?\d+))?(%?)$/.exec(t)
  if (!m || (m[2] === '' && !m[3])) return undefined
  let v = Number(`${m[1]}${m[2]!.replace(/,/g, '')}${m[3] ?? ''}${m[4] ? `e${m[4]}` : ''}`)
  if (m[5]) v /= 100
  if (!Number.isFinite(v) || Math.abs(v) > 1e15) return undefined
  return v
}

/** A cell's value shown: "48,200", "0.25" */
export function cellText(v: number): string {
  return v.toLocaleString('ko-KR', { maximumFractionDigits: 10 })
}

// ---- formulas of computed tensors (defined names) ----

const IDENT_START = /[A-Za-z_\\\u0080-￿]/
const IDENT_PART = /[A-Za-z0-9_.\\\u0080-￿]/

/**
 * The defined names a formula reads, of the given ones (any case of ASCII
 * letters, as the engine reads names): names outside "strings", 'sheet
 * names'! and [slices] (dimension names and items there), not calls f(…)
 * and not sheet names before "!".
 */
export function nameRefs(formula: string, names: Iterable<string>): string[] {
  const known = new Map<string, string>()
  for (const n of names) known.set(n.toLowerCase(), n)
  const out = new Set<string>()
  const f = formula
  for (let i = 0; i < f.length; ) {
    const ch = f[i]!
    if (ch === '"') {
      i++
      while (i < f.length && !(f[i] === '"' && f[i + 1] !== '"')) i += f[i] === '"' ? 2 : 1
      i++
      continue
    }
    if (ch === "'") {
      i++
      while (i < f.length && !(f[i] === "'" && f[i + 1] !== "'")) i += f[i] === "'" ? 2 : 1
      i++
      continue
    }
    if (ch === '[') {
      const end = f.indexOf(']', i)
      i = end < 0 ? f.length : end + 1
      continue
    }
    if (IDENT_START.test(ch)) {
      let j = i + 1
      while (j < f.length && IDENT_PART.test(f[j]!) && !(f[j] === '.' && f[j + 1] === '.')) j++
      const word = f.slice(i, j)
      let k = j
      while (f[k] === ' ') k++
      const next = f[k]
      if (next !== '(' && next !== '!') {
        const hit = known.get(word.toLowerCase())
        if (hit !== undefined) out.add(hit)
      }
      i = j
      continue
    }
    i++
  }
  return [...out]
}

/**
 * A name that would come back to itself: the path of names (first and last
 * the candidate), or null. `names`: the workbook's defined names with the
 * candidate's new formula in place of its old one (or added).
 */
export function findCycle(names: readonly { name: string; formula: string }[], candidate: string): string[] | null {
  const byLow = new Map(names.map((n) => [n.name.toLowerCase(), n]))
  const all = names.map((n) => n.name)
  const refs = new Map<string, string[]>()
  const refsOf = (low: string): string[] => {
    let r = refs.get(low)
    if (!r) {
      const n = byLow.get(low)
      r = n ? nameRefs(n.formula, all).map((x) => x.toLowerCase()) : []
      refs.set(low, r)
    }
    return r
  }
  const start = candidate.toLowerCase()
  const seen = new Set<string>()
  const path: string[] = []
  const walk = (low: string): boolean => {
    for (const next of refsOf(low)) {
      if (next === start) {
        path.push(next)
        return true
      }
      if (seen.has(next)) continue
      seen.add(next)
      path.push(next)
      if (walk(next)) return true
      path.pop()
    }
    return false
  }
  if (!walk(start)) return null
  return [start, ...path].map((low) => byLow.get(low)?.name ?? low)
}

/** The top-level arguments of calls to fn in a formula (strings and nesting kept whole). */
function callArgs(formula: string, fn: RegExp): string[][] {
  const out: string[][] = []
  for (const m of formula.matchAll(fn)) {
    const before = m.index === 0 ? '' : formula[m.index - 1]!
    if (before && IDENT_PART.test(before)) continue // part of a longer name
    let i = m.index + m[0].length
    const args: string[] = []
    let depth = 0
    let cur = ''
    for (; i < formula.length; i++) {
      const ch = formula[i]!
      if (ch === '"') {
        const end = formula.indexOf('"', i + 1)
        cur += formula.slice(i, end < 0 ? formula.length : end + 1)
        i = end < 0 ? formula.length : end
        continue
      }
      if (ch === '(' || ch === '[' || ch === '{') depth++
      if (ch === ')' || ch === ']' || ch === '}') {
        if (depth === 0) break
        depth--
      }
      if (ch === ',' && depth === 0) {
        args.push(cur.trim())
        cur = ''
        continue
      }
      cur += ch
    }
    args.push(cur.trim())
    out.push(args)
  }
  return out
}

/**
 * shift, cumsum and diff go along an ordered dimension (월, or one made
 * ordered); along one whose order is only for showing they are refused
 * (design 02 3.4). Returns why, or null.
 */
export function orderProblem(formula: string, categoryDims: Iterable<string>): string | null {
  const cats = new Set(categoryDims)
  for (const args of callArgs(formula, /\b(shift|cumsum|diff)\s*\(/gi)) {
    const dim = args[1]
    if (dim !== undefined && cats.has(dim))
      return `shift·cumsum·diff는 순서가 있는 차원(월, 순서 차원)에만 씁니다. '${dim}'의 순서는 보기용입니다.`
  }
  return null
}

/** What a formula's result is: a tensor over named dimensions, a grid array, one value, or an error. */
export type Shape =
  | { kind: 'tensor'; dims: string[] }
  | { kind: 'array'; rows: number; cols: number }
  | { kind: 'value' }
  | { kind: 'error'; error: string }

export function shapeOf(v: EvalValue | undefined): Shape {
  if (v === undefined) return { kind: 'error', error: '#CALC!' }
  if (v !== null && typeof v === 'object') {
    if ('dims' in v) return { kind: 'tensor', dims: v.dims.map((d) => d.name) }
    if ('array' in v) return { kind: 'array', rows: v.array.length, cols: v.array[0]?.length ?? 0 }
    if ('error' in v) return { kind: 'error', error: v.error }
  }
  return { kind: 'value' }
}

/** "[월, 분류]", "3×2 배열", "값 하나", "#DIM!" */
export function shapeText(s: Shape): string {
  switch (s.kind) {
    case 'tensor':
      return `[${s.dims.join(', ')}]`
    case 'array':
      return `${s.rows}×${s.cols} 배열`
    case 'value':
      return '값 하나'
    case 'error':
      return s.error
  }
}

export interface NameCheck {
  ok: boolean
  /** what to tell the person (why not, or a warning) */
  message: string | null
  shape: Shape | null
}

/** How the engine is asked (GridStore.evaluate, or a calc client in tests). */
export type Evaluate = (formulas: string[]) => Promise<({ ok: boolean; value?: EvalValue; error?: string } | undefined)[]>

/**
 * The checks before a computed tensor (a defined name) is saved (design 02
 * 3.4). `names`: the workbook's names now; `categoryDims`: the names of
 * dimensions whose order is only for showing.
 */
export async function checkName(
  candidate: { name: string; formula: string },
  names: readonly { name: string; formula: string }[],
  categoryDims: Iterable<string>,
  evaluate: Evaluate,
): Promise<NameCheck> {
  const formula = candidate.formula.trim().replace(/^=/, '')
  if (formula === '') return { ok: false, message: '수식을 적어 주세요.', shape: null }
  const low = candidate.name.trim().toLowerCase()
  const list = names.filter((n) => n.name.toLowerCase() !== low).concat([{ name: candidate.name.trim(), formula }])
  const cycle = candidate.name.trim() ? findCycle(list, candidate.name.trim()) : null
  if (cycle) return { ok: false, message: `순환 참조라 저장하지 않습니다: ${cycle.join(' → ')}`, shape: null }
  const order = orderProblem(formula, categoryDims)
  if (order) return { ok: false, message: order, shape: null }
  const [a] = await evaluate(['=' + formula])
  if (!a || !a.ok) return { ok: false, message: `수식을 읽을 수 없습니다${a?.error ? `: ${a.error}` : ''}`, shape: null }
  const shape = shapeOf(a.value)
  if (shape.kind === 'error' && shape.error === '#DIM!')
    return { ok: false, message: '차원이 서로 맞지 않아(#DIM!) 저장하지 않습니다.', shape }
  if (shape.kind === 'error') return { ok: true, message: `결과가 ${shape.error}입니다. 그래도 저장할 수 있습니다.`, shape }
  return { ok: true, message: null, shape }
}
